import { createIntl, createIntlCache } from "react-intl";
import { Components } from '../../global.d';
import {
  disclosuresType,
  DisclosureValue,
  ITradeDetails,
  NeedMoreStatus,
  OdometerType,
  OdometerTypeLabel,
  TradeState,
  TransmissionType,
  TransmissionTypeLabel
} from '../types/trade';
import { BidStatus } from '../types/auction'
import { TagContent, TagStyle } from '../types/tags';
import Tag from './Tags';
import User from './User';
import { formatDate, formatNumber } from '../utils/localization';
import NegotiationState from "../constants/negotiationState";
import systemConfigs from '../constants/systemConfigs';
import UpcomingProxyBid from './UpcomingProxyBid';
import Bid from './Bid';
import renderLocale from "../helpers/renderLocale";
import messages from '../../assets/messages';

const locale = renderLocale();
const intl = createIntl(
  { locale, messages: messages[locale] },
  createIntlCache()
);

const labels = {
  noQuoteAvailable: 'No quote available',
  isOperable: {
    yes: 'Yes',
    no: 'No',
    na: 'N/A'
  },
  needsRepair: 'Needs Repair',
  cylinder: 'cyl',
  litres: 'L',
  asIs: 'AS-IS',
  noClosing: 'NO CLOSING',
  inStock: 'IN-STOCK',
  disclosures: 'DISCLOSURES',
  qa: 'Q&A',
  away: 'away'
};

const getIsOperable = isOperable => {
  switch (isOperable) {
    case false:
      return labels.isOperable.no;
    case true:
      return labels.isOperable.yes;
    default:
      return labels.isOperable.na;
  }
};

const getHasModifications = hasModifications => {
  switch (hasModifications) {
    case true:
      return labels.isOperable.yes;
    case false:
      return labels.isOperable.no;
    default:
      return labels.isOperable.na;
  }
};

const getMileage = (tradeDetails): string => {
  if (!tradeDetails?.mileage && tradeDetails.mileage !== 0) {
    return '-';
  }

  const mileage = new Intl.NumberFormat(locale).format(tradeDetails.mileage);

  let unit = OdometerTypeLabel.Kilometre;

  if (tradeDetails.odometerType === OdometerType.Miles) {
    unit = OdometerTypeLabel.Miles;
  }

  return `${mileage} ${unit}`;
};

const getFilteredVehicleHistory = (vehicleHistory: Array<any>) => {
  return vehicleHistory.filter(disclosure => {
    return disclosure.value === DisclosureValue.Present;
  });
};

const getFilteredVehicleCondition = (vehicleCondition: Array<any>) => {
  return vehicleCondition.filter(disclosure => {
    const parsedDisclosureValue = parseInt(disclosure.value, 10);
    return (
      !disclosure.positiveValues.includes(parsedDisclosureValue) &&
      parsedDisclosureValue !== DisclosureValue.Empty
    );
  });
};

const getFilteredVehicleNeedRepair = (vehicleNeedRepair: Array<any>) => {
  return vehicleNeedRepair.filter(disclosure => {
    return disclosure.value === DisclosureValue.Present;
  });
};

const getFilteredVehicleDeclaration = (vehicleDeclaration: Array<any>) => {
  return vehicleDeclaration.filter(disclosure => {
    return !disclosure.positiveValues.includes(parseInt(disclosure.value, 10));
  });
};

const getTotalNumberOfDisclosures = (tradeDetails): number => {
  return (
    getFilteredVehicleHistory(tradeDetails.vehicleHistory).length +
    getFilteredVehicleCondition(tradeDetails.vehicleCondition).length +
    getFilteredVehicleNeedRepair(tradeDetails.vehicleNeedRepair).length +
    getFilteredVehicleDeclaration(tradeDetails.vehicleDeclaration).length +
    (tradeDetails.hasAccident ? 1 : 0)
  );
};

const getTransmissionTypeFormatted = (
  transmission: TransmissionType
): string | null => {
  switch (transmission) {
    case TransmissionType.Automatic:
      return TransmissionTypeLabel.Automatic;
    case TransmissionType.Manual:
      return TransmissionTypeLabel.Manual;
    default:
      return null;
  }
};

const getAvailableDateFormatted = (availableDate: string, tradeDetails?) => {
  const options = { month: 'short', day: 'numeric', year: 'numeric' };

  if (tradeDetails?.isLanded) {
    return intl.formatMessage({id: "badges.inStock"});
  }

  return availableDate ? formatDate(options, new Date(availableDate)) : '';
};

const getDisclosureValueDisplayName = disclosure => {
  const displayName = disclosure?.possibleValues?.find(
    d => d.key === disclosure.value?.toString()
  );

  return displayName?.value || '';
};

const getVehicleConditionWithDisplayText = vehicleCondition => {
  return vehicleCondition.map(vc => {
    return {
      ...vc,
      displayText: `${vc.displayName} ${getDisclosureValueDisplayName(vc)}`
    };
  });
};

const getVehicleNeedRepairWithDisplayText = vehicleNeedRepair => {
  const { needsRepair } = labels; // TODO translation
  return vehicleNeedRepair.map(nr => {
    return {
      ...nr,
      displayText: `${nr.displayName} ${needsRepair}`
    };
  });
};

const getVehicleDeclarationWithDisplayText = vehicleDeclaration => {
  return vehicleDeclaration.map(vc => {
    const element = { ...vc };
    if (element.valueType === disclosuresType.type.integer) {
      element.displayText = `${element.displayName
      } ${getDisclosureValueDisplayName(element)}`;
    }
    return element;
  });
};

const addDisclosureDisplayText = disclosures => {
  return {
    ...disclosures,
    displayText: disclosures.displayText || disclosures.displayName
  };
};

const getSystemConfigValue = (key, configurations) => {
  return configurations?.find(configuration => {
    return configuration.key === key;
  })?.value;
};

export const getBidIncrement = (minBidThreshold, configurations, bidAmount) => {
  const currentThreshold = bidAmount || minBidThreshold;
  const threshold = parseInt(
    getSystemConfigValue(systemConfigs.BID_THRESHOLD, configurations) || 1000,
    10
  );
  if (!currentThreshold || currentThreshold < threshold) {
    return parseInt(
      getSystemConfigValue(
        systemConfigs.BID_INCREMENT_BEFORE,
        configurations
      ) || 50,
      10
    );
  }
  return parseInt(
    getSystemConfigValue(systemConfigs.BID_INCREMENT_AFTER, configurations) ||
    100,
    10
  );
};

const hasActiveProxy = (trade) => {
  if (trade?.draftMode || !(trade?.auctionSummary)) {
    return false;
  }
  return (
    trade.auctionSummary.userBidStatus ===
    BidStatus.proxy
  );
}

const isTopBidder = (trade) => {
  if (!trade?.auctionSummary) {
    return false;
  }
  return (
    trade.auctionSummary?.userBidStatus ===
    BidStatus.topBidder ||
    trade.auctionSummary?.userBidStatus ===
    BidStatus.proxy
  );
}

const getAutoniqUrl = (configurations) => {
  return getSystemConfigValue(systemConfigs.AUTONIQ_LINK, configurations)
}

interface tradeDetailsParams {
  configurations?: any;
  countryId?: any;
  dealer?: any;
  isFetching?: boolean;
  liveBid?: any;
  manufacturers?: any;
  trade?: any;
  user?: any;
}
class TradeDetails implements ITradeDetails {
  // Reason prettier does not yet support private field syntax
  // TODO update prettier refer to https://github.com/prettier/prettier/issues/7263
  // eslint-disable-next-line
  #trade;

  associatedTrade;

  auctionLaunchCount;

  auctionSummary;

  autoniqUrl;

  bid;

  billOfSale;

  chatStatus;

  cylinders;

  dateAvailable;

  dealer;

  dealerInfo;

  decodedAt;

  disclosures;

  displacement;

  driveTrain;

  exteriorColor;

  fuelType;

  hasDateExpectedExceededLimit;

  id;

  interiorColor;

  isAsIs;

  isImpounded;

  isLanded;

  isLoading;

  isMscCertified;

  isSelling;

  isWatchlisted;

  manufacturerName;

  maxBid;

  modelName;

  needMoreStatus;

  network;

  notes;

  numberOfBids;

  odometerDisplay;

  options;

  passengers;

  pickupAddress;

  saleEventId;

  saleEventSummary;

  statusDates;

  tags;

  titleInHand;

  tradeFloorPrice;

  tradeInCustomerInfo;

  tradeState;

  transmission;

  transmissionDriveTrain;

  transportInfo;

  trim;

  upcomingProxyBid;

  vehicleHistoryReportUrl;

  vehicleSummary;

  vin;

  year;


  constructor(tradeDetailsParams: tradeDetailsParams = {}) {
    const {trade, dealer, countryId, liveBid, configurations, manufacturers, user, isFetching} = tradeDetailsParams;
    this.#trade = liveBid
      ? TradeDetails.updateTradeWithLiveBid(trade, liveBid)
      : trade;

    const tradeDetails = this.#trade?.details;
    const dateAvailable = this.#trade?.dateAvailable || this.#trade?.statusDates?.available;
    const tradeVehicleSummary = tradeDetails?.tradeVehicleSummary;
    this.id = trade?.id;
    this.auctionSummary = this.#trade?.auctionSummary;
    this.year = tradeVehicleSummary?.vehicleSummary?.modelYear;
    this.vehicleSummary = tradeDetails?.tradeVehicleSummary?.vehicleSummary;
    this.manufacturerName = TradeDetails.getManufacturerName(tradeVehicleSummary?.vehicleSummary, manufacturers);
    this.modelName = tradeVehicleSummary?.vehicleSummary?.model?.displayName;
    this.numberOfBids = this.auctionSummary?.numBids
      ? `${this.auctionSummary.numBids} bids`
      : '0 bids';
    this.maxBid = TradeDetails.formatMaxBid(this.auctionSummary?.maxBid);
    this.odometerDisplay = tradeDetails ? getMileage(tradeDetails) : null;
    this.trim = tradeVehicleSummary?.vehicleSummary?.trim;
    this.transmission = getTransmissionTypeFormatted(
      tradeDetails?.transmissionType.id
    );
    this.notes = tradeDetails?.notes;
    this.dateAvailable = dateAvailable
      ? getAvailableDateFormatted(dateAvailable, tradeDetails)
      : null;
    this.vin = tradeDetails?.vin;
    this.driveTrain = tradeDetails?.drivetrain?.displayName;
    this.cylinders = tradeDetails?.cylinders
      ? `${tradeDetails.cylinders}${labels.cylinder}`
      : null;
    this.displacement = tradeDetails?.displacement
      ? `${tradeDetails.displacement}${labels.litres}`
      : null;
    this.fuelType = tradeDetails?.fuelType?.displayName;
    this.exteriorColor = tradeDetails?.exteriorColor?.displayName;
    this.interiorColor = tradeDetails?.interiorColor?.displayName;
    this.tags = TradeDetails.createTags(trade);
    this.pickupAddress = TradeDetails.getAddress(tradeDetails?.pickupAddress);
    this.disclosures = TradeDetails.getDisclosures(tradeDetails);
    this.vehicleHistoryReportUrl = trade?.mediaCounts?.vehicleHistoryReportUrl || null;
    this.options = TradeDetails.getTradeOptions(tradeDetails?.options);
    this.transportInfo = TradeDetails.getTransportInfo(
      trade,
      dealer,
      countryId,
      configurations
    );
    this.isAsIs = tradeDetails?.isAsIs
    this.isWatchlisted = trade?.isWatchlisted;
    this.isImpounded = trade?.isImpounded;
    this.transmissionDriveTrain = TradeDetails.transmissionDriveTrain(this);
    this.powerTrain = TradeDetails.powerTrain(this);
    this.titleInHand = TradeDetails.titleInHand(this);
    this.passengers = tradeDetails?.passengers
    this.upcomingProxyBid = new UpcomingProxyBid(
      this.auctionSummary?.minBidThreshold,
      getBidIncrement(this.auctionSummary?.minBidThreshold, configurations, null),
      tradeDetails?.upcomingProxy?.bidAmount,
      tradeDetails?.upcomingProxy?.proxyAmount
    );
    this.bid = new Bid(
      this.auctionSummary?.minBidThreshold,
      this.auctionSummary?.highBidThreshold,
      getBidIncrement(this.auctionSummary?.minBidThreshold, configurations, this.auctionSummary?.maxBid),
      this.auctionSummary?.maxBid,
      this.auctionSummary?.proxyAmount,
      this.auctionSummary?.topBidderSummary,
      this.auctionSummary?.isTimerExtended,
      hasActiveProxy(this.#trade),
      isTopBidder(this.#trade)
    );
    this.dealer = TradeDetails.getDealerInfo(trade);
    this.chatStatus = trade?.status?.chatStatus;
    this.needMoreStatus = trade?.status?.needMoreStatus;
    this.autoniqUrl = getAutoniqUrl(configurations);
    this.isMscCertified = trade?.isMscCertified;
    this.statusDates = trade?.statusDates;
    this.hasDateExpectedExceededLimit = tradeDetails?.isDateExpectedExceedLimit;
    this.tradeFloorPrice = tradeDetails?.tradeFloorPrice;
    this.isLanded = tradeDetails?.isLanded;
    this.tradeState = this.#trade?.tradeState;
    this.isSelling = user?.dealerId === this.dealer?.id;
    this.associatedTrade = this.#trade?.associatedTrade;
    this.network = this.#trade?.network;
    this.tradeInCustomerInfo = tradeDetails?.tradeInCustomerInfo || this.#trade?.details?.tradeInCustomerInfo;
    this.decodedAt = tradeDetails?.decodedAt || this.#trade?.details?.decodedAt;
    this.billOfSale = tradeDetails?.billOfSale;
    this.auctionLaunchCount = tradeDetails?.auctionLaunchCount;
    this.saleEventId = trade?.saleEventId;
    this.saleEventSummary = tradeDetails?.saleEventSummary;
    this.isLoading = isFetching;
  }

  static transmissionDriveTrain(tradeDetails) {
    return `${tradeDetails.transmission}, ${tradeDetails.driveTrain}, ${TradeDetails.powerTrain(tradeDetails)}`;
  }

  static powerTrain(tradeDetails) {
    const displacement = tradeDetails.displacement ? `${tradeDetails.displacement}` : '';
    const cylinders = tradeDetails.cylinders ? `${tradeDetails.cylinders}` : ''
    const space = tradeDetails.displacement && tradeDetails.cylinders ? ' ' : '';
    const comma = tradeDetails.displacement || tradeDetails.cylinders ? ', ' : '';
    return `${displacement}${space}${cylinders}${comma}${tradeDetails.fuelType}`;
  }

  static titleInHand(tradeDetails) {
    const title = tradeDetails?.disclosures.find((declaration) => {
      return declaration.id === 5
    });

    return title?.value ? 'No' : 'Yes';
  }

  static createTags(trade): Array<Tag> {
    const tradeDetails = trade?.details;
    const chatNumber = trade?.status?.chatStatus?.chatCount;

    const isAsIs = tradeDetails?.isAsIs ? labels.asIs : null;
    const { noClosing } = labels; // TODO compute this value
    const inStock = tradeDetails?.isLanded ? labels.inStock : null;
    const numDisclosures = tradeDetails
      ? `${getTotalNumberOfDisclosures(tradeDetails)} ${labels.disclosures}`
      : null;
    const chatCount = chatNumber ? `${chatNumber} ${labels.qa}` : null;

    const tags = {
      isAsIs,
      noClosing,
      inStock,
      numDisclosures,
      chatCount
    };

    const formattedTags = [];

    Object.keys(TagContent).forEach(tag => {
      if (!tags[tag]) {
        return;
      }

      const tagStyle =
        tags[tag] === labels.asIs ? TagStyle.danger : TagStyle.default;
      formattedTags.push(new Tag(TagContent[tag], tagStyle, tags[tag]));
    });

    return formattedTags;
  }

  static getManufacturerName(vehicleSummary, manufacturers) {
    let manufacturerName = '';
    if (vehicleSummary && manufacturers) {
      const manufacturerId = Object.keys(manufacturers).find(
        key => manufacturers[key].id === vehicleSummary.model.manufacturerId
      );

      manufacturerName = manufacturers[manufacturerId]?.displayName;
    }

    return manufacturerName;
  }

  static getTradeOptions(options) {
    const presentOptions = [];

    if (!options) {
      return presentOptions;
    }

    options.forEach(optionGroup => {
      optionGroup.options.forEach(option => {
        if (option.value) {
          presentOptions.push(option.displayName);
        }
      });
    });

    return presentOptions;
  }

  static getAddress(address) {
    if (!address) {
      return null;
    }

    return {
      alias: address.alias,
      street: address.street,
      city: address.city,
      province: address.province,
      postalCode: address.postalCode,
      isAdesaPhysicalLocation: address.isAdesaPhysicalLocation
    };
  }

  static getDisclosures(details) {
    if (!details) {
      return [];
    }

    return details.vehicleHistory
      .concat(getVehicleConditionWithDisplayText(details.vehicleCondition))
      .concat(getVehicleNeedRepairWithDisplayText(details.vehicleNeedRepair))
      .concat(getVehicleDeclarationWithDisplayText(details.vehicleDeclaration))
      .filter(d => {
        if (!Array.isArray(d.positiveValues)) {
          return !!d.value;
        }
        return !d.positiveValues.includes(parseInt(d.value, 10));
      })
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(addDisclosureDisplayText);
  }

  // TODO make this into a custom model
  static getTransportInfo(trade, dealer, countryId, configurations) {
    const odometerType = User.isCountryCanada(countryId)
      ? OdometerTypeLabel.Kilometre
      : OdometerTypeLabel.Miles;

    const sellerFullName = trade?.appraisedBy?.fullName;
    const dealerName = trade?.appraisedBy?.companyName;
    const isOperableVehicle = User.isCountryCanada(countryId) ? getIsOperable(trade?.details?.isOperable) : labels.isOperable.yes;
    const isModified = User.isCountryCanada(countryId) ? getHasModifications(trade?.details?.hasModifications) : labels.isOperable.no;
    const pickupLocation = TradeDetails.getAddress(
      trade?.details?.pickupAddress
    );
    const transportDistance = dealer?.distance
      ? `${dealer?.distance} ${odometerType} ${labels.away}`
      : null;

    const buyerDeliveryAddress = TradeDetails.getAddress(
      trade?.dealer?.deliveryAddress
    );

    const dateExpected = trade?.details?.isLanded ? intl.formatMessage({id: 'badges.inStock'}) : getAvailableDateFormatted(trade?.statusDates?.available);

    const hasDateExpectedExceededLimit = trade?.details?.isDateExpectedExceedLimit;

    const disclosureCount = (isOperableVehicle === labels.isOperable.no ? 1 : 0) + (isModified === labels.isOperable.yes ? 1 : 0)
      + (hasDateExpectedExceededLimit ? 1 : 0);

    const dateExpectedDaysLimit = getSystemConfigValue(systemConfigs.DATE_EXPECTED_LIMIT, configurations);

    return {
      sellerFullName,
      dealerName,
      isOperableVehicle,
      isModified,
      pickupLocation,
      transportDistance,
      buyerDeliveryAddress,
      dateExpected,
      disclosureCount,
      hasDateExpectedExceededLimit,
      dateExpectedDaysLimit
    };
  }

  static formatMaxBid(maxBid) {
    if (!maxBid) {
      return `$0`;
    }

    return formatNumber(
      {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      },
      maxBid
    );
  }

  static updateAuctionSummary(auctionSummary, liveBid) {
    return {
      ...auctionSummary,
      numBids: liveBid.bidCount,
      maxBid: liveBid.maxBidAmount,
      topBidderSummary: liveBid.topBidder,
      isTimerExtended: liveBid.isTimerExtended
    };
  }

  static updateTradeWithLiveBid(trade, liveBid) {
    const updatedTrade = { 
      ...trade, 
      details: trade.details ? { ...trade.details } : undefined
    };
    
    const auctionSummary = TradeDetails.updateAuctionSummary(
      trade?.auctionSummary,
      liveBid
    );

    if (liveBid.closingTime) {
      if (updatedTrade.details) {
        updatedTrade.details.activeExpiryTime = liveBid.activeExpiryTime;
      } else {
        updatedTrade.activeExpiryTime = liveBid.activeExpiryTime;
      }
    }

    updatedTrade.auctionSummary = auctionSummary;

    return updatedTrade;
  }

  static getDealerInfo(trade) {
    const id = trade?.dealer?.id;
    const displayName = trade?.dealer?.displayName;
    const address = trade?.dealer?.address;
    const appraisedBy = trade?.appraisedBy;
    const rating = trade?.dealer?.rating;
    const acceptedPayments = trade?.details?.acceptedPayments;
    const network = trade?.network;
    const ratingCount = trade?.dealer?.ratingCount;
    const isTrdSeller = trade?.dealer?.isTrdSeller;
    const customerId = trade?.dealer?.customerId;

    return { id, appraisedBy, address, displayName, customerId, rating, acceptedPayments, network, ratingCount, isTrdSeller };
  }

  static asPendingTrade(
    tradeDetails: TradeDetails,
    negotiationState: NegotiationState | null
  ): Components.Schemas.PendingTrade {
    return {
      id: tradeDetails?.id,
      auctionId: tradeDetails?.auctionSummary?.id,
      dealer: tradeDetails?.dealer,
      isAsIs: tradeDetails?.isAsIs,
      year: tradeDetails?.year,
      make: { name: tradeDetails?.manufacturerName },
      model: { name: tradeDetails?.modelName },
      topBid: tradeDetails?.auctionSummary?.maxBid,
      isSelling: tradeDetails?.isSelling,
      isInternalTrade: tradeDetails?.associatedTrade?.network
        && !tradeDetails?.associatedTrade?.network?.isDealerGroupNetwork,
      negotiationState: negotiationState || null,
      associatedTradeId: tradeDetails?.associatedTrade?.tradeId,
      tradeState: tradeDetails?.tradeState,
      unusedProxyExists: tradeDetails?.auctionSummary?.unusedProxyExists,
      sellingNetworkId: tradeDetails?.network?.id,
      tradeInCustomerInfo: tradeDetails?.tradeInCustomerInfo,
      decodedAt: tradeDetails?.decodedAt,
      vin: tradeDetails?.vin,
      trim: tradeDetails?.trim,
    };
  }

  static canNegotiate(tradeDetails) {
    return tradeDetails?.tradeState === TradeState.pending &&
      (!tradeDetails?.isSelling ||
      !tradeDetails?.associatedTrade?.network?.isDealerGroupNetwork ||
      tradeDetails?.associatedTrade?.tradeState === TradeState.lost);
  }

  static getInProgressNegotiationState(
    isSeller: boolean,
    isActionRequired: boolean,
    counterExists: boolean
  ): NegotiationState {
    if (isSeller) {
      if (isActionRequired) {
        return counterExists
          ? NegotiationState.BUYER_COUNTER
          : NegotiationState.BUYER_OFFER;
      }
      return counterExists
        ? NegotiationState.SELLER_COUNTER
        : NegotiationState.SELLER_OFFER;
    }
    if (isActionRequired) {
      return counterExists
        ? NegotiationState.SELLER_COUNTER
        : NegotiationState.SELLER_OFFER;
    }
    return counterExists
      ? NegotiationState.BUYER_COUNTER
      : NegotiationState.BUYER_OFFER;
  }

  static getNegotiationState(
    tradeDetails: TradeDetails,
    counterExists: boolean
  ): NegotiationState {
    const status = tradeDetails?.needMoreStatus?.status;

    const isSeller = tradeDetails?.needMoreStatus?.negotiationPosition
      === NeedMoreStatus.negotiationPosition.Seller;

    const isActionRequired = status
      === NeedMoreStatus.status.ActionRequired;

    switch (status) {
      case NeedMoreStatus.status.NotStarted:
        return NegotiationState.NO_NEGOTIATION;
      case NeedMoreStatus.status.Rejected:
        return NegotiationState.DECLINED;
      case NeedMoreStatus.status.Accepted:
        return NegotiationState.ACCEPTED;
      case NeedMoreStatus.status.Inapplicable:
        return NegotiationState.FINAL_ASKING_PRICE;
      default:
        return TradeDetails.getInProgressNegotiationState(
          isSeller,
          isActionRequired,
          counterExists
        );
    }
  }

  static getLatestProposalPrice(tradeDetails: TradeDetails) {
    return tradeDetails?.needMoreStatus?.currentProposal;
  }
}

export default TradeDetails;
